import {SchemaFieldColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {doSnapshotFieldPreviewLookupQuery} from 'notebook/structured-tables/snapshot-fields/SnapshotFieldPreviewLookupDAO';
import {
  parseIdFromLookupInputTypedValues,
  parseTextValueFromLookupInputTypedValue,
} from 'notebook/structured-tables/util/TypedValueAdapters';
import {CustomEvalResult} from 'notebook/tables/constants/CustomEvalConstants';
import {
  CustomResolutionQueryType,
  ServerResolutionResult,
  SnapshotFieldPreviewQuery,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
  ResolveTypedValueToTypeFn,
} from 'notebook/tables/typed-values/TypedValueResolution';

export async function bulkResolveSnapshotFieldPreviewToServerResolutionResult(
  queries: Array<SnapshotFieldPreviewQuery>
): Promise<Array<ServerResolutionResult>> {
  if (!queries.every((query) => query.snapshotFieldId === queries[0].snapshotFieldId)) {
    throw Error('Expected all batched lookup queries to have the same lookupConfigID');
  }
  if (!queries.every((query) => query.fileId === queries[0].fileId)) {
    throw Error('Expected all batched lookup queries to come from the same document');
  }

  const queryResult = await doSnapshotFieldPreviewLookupQuery(
    queries[0].snapshotFieldId,
    queries[0].fileId,
    queries.map((q) => q.inputValueIds),
    queries.map((q) => q.gridPositionInputValue)
  );

  return queryResult.serverResolutionResults;
}

export function snapshotFieldPreviewCustomEval(
  typedValue: TypedValue,
  columnDefinition: SchemaFieldColumnDefinition,
  cellDependencies: [TypedValue | null, TypedValue | null],
  customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn,
  resolveTypedValueToType: ResolveTypedValueToTypeFn,
  fileId?: string | null
): CustomEvalResult {
  const inputTypedValue = cellDependencies[0];
  if (!inputTypedValue) {
    return {
      val: {type: BaseTypedValueType.STRING, rawValue: ''},
      error: null,
      locked: true,
    };
  }

  // For now we only allow two input values per row: 1 with id(s), 1 for text field
  const inputValueIds = parseIdFromLookupInputTypedValues(inputTypedValue);
  if (!inputValueIds.length) {
    return {
      val: {type: BaseTypedValueType.STRING, rawValue: ''},
      error: null,
      locked: true,
    };
  }

  const secondInputTypedValue = cellDependencies[1];
  const gridPositionInputValue = secondInputTypedValue
    ? parseTextValueFromLookupInputTypedValue(secondInputTypedValue)
    : null;

  const {resolvedValue, error} = resolveFromResolutionQuery(
    typedValue,
    {
      customResolutionQueryType: CustomResolutionQueryType.SNAPSHOT_FIELD_PREVIEW,
      inputValueIds,
      gridPositionInputValue,
      fileId: fileId ?? null,
      snapshotFieldId: columnDefinition.tagSchemaFieldApiId,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );

  return {val: resolvedValue, error, locked: true};
}
